import type { Surface } from '@funkit/connect-core'
import type { ReactNode } from 'react'

import type {
  ClaimedSurface,
  SurfaceAdapter,
  SurfaceContext,
  SurfaceControlModel,
} from './providers/adapter'
import { banxaRedirect } from './providers/banxaRedirect'
import { coinbaseApplePay } from './providers/coinbaseApplePay'
import { moonpay } from './providers/moonpay'
import { sumsubKyc } from './providers/sumsubKyc'
import { transakApplePay } from './providers/transakApplePay'

/**
 * Every surface this build can draw, first match wins. A surface no adapter
 * claims lands on the screen's unsupported state rather than rendering
 * nothing, so adding a provider is one entry here plus its adapter.
 */
const ADAPTERS: readonly SurfaceAdapter[] = [
  transakApplePay,
  coinbaseApplePay,
  moonpay,
  banxaRedirect,
  sumsubKyc,
]

/**
 * What the first adapter that claims this surface will draw, and how it is
 * operated — or `undefined` when none claims it.
 */
function findClaimedSurface(
  surface: Surface,
  extra: readonly SurfaceAdapter[] = [],
): ClaimedSurface | undefined {
  for (const adapter of [...extra, ...ADAPTERS]) {
    const claimed = adapter(surface)
    if (claimed !== undefined) {
      return claimed
    }
  }
  return undefined
}

/**
 * How this surface is operated, or `undefined` when nothing can draw it.
 * Asked before rendering, because the two models want different chrome.
 */
export function captureControl(
  surface: Surface,
  adapters: readonly SurfaceAdapter[] = [],
): SurfaceControlModel | undefined {
  return findClaimedSurface(surface, adapters)?.control
}

/**
 * `adapters` are tried before the registry, for a screen that can build one
 * the registry cannot — the identity SDK needs the quote its capture belongs
 * to, which the surface itself does not carry.
 */
export function renderCaptureSurface({
  surface,
  adapters,
  ...context
}: SurfaceContext & {
  surface: Surface
  adapters?: readonly SurfaceAdapter[]
}): ReactNode {
  return findClaimedSurface(surface, adapters)?.render(context) ?? null
}
